import { db, auth } from './firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  writeBatch,
  Timestamp,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
} from 'firebase/auth';

const FIREBASE_API_KEY = process.env.REACT_APP_FIREBASE_API_KEY;

// ── Auth ──

async function signIn(email, password) {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return { user: result.user };
}

async function signOutUser() {
  await firebaseSignOut(auth);
}

function onAuthStateChanged(callback) {
  return firebaseOnAuthStateChanged(auth, callback);
}

// ── Users ──

async function getUserProfile(uid) {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { uid: snap.id, ...snap.data() };
}

async function getUsers() {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs
    .map((d) => ({ uid: d.id, ...d.data() }))
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
}

async function createUser({ email, password, displayName, role }) {
  // Use Firebase Auth REST API to create user without signing out admin
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FIREBASE_API_KEY}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnSecureToken: false }),
    }
  );
  const data = await res.json();
  if (data.error) {
    throw new Error(data.error.message || 'Failed to create user');
  }
  const uid = data.localId;

  // Create Firestore user document
  await setDoc(doc(db, 'users', uid), {
    email,
    displayName,
    role,
    status: 'offline',
    lastSeen: Timestamp.now(),
    createdAt: serverTimestamp(),
  });

  return { uid, email, displayName, role, status: 'offline' };
}

async function createUserProfile(uid, { email, displayName, role }) {
  await setDoc(doc(db, 'users', uid), {
    email,
    displayName,
    role,
    status: 'offline',
    lastSeen: Timestamp.now(),
    createdAt: serverTimestamp(),
  });
  const snap = await getDoc(doc(db, 'users', uid));
  return { uid: snap.id, ...snap.data() };
}

async function updateUser(uid, data) {
  const ref = doc(db, 'users', uid);
  await updateDoc(ref, data);
  const snap = await getDoc(ref);
  return { uid: snap.id, ...snap.data() };
}

async function deleteUser(uid) {
  await deleteDoc(doc(db, 'users', uid));
}

// ── Profile Picture ──

function resizeImage(file, maxSize = 200) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width;
      let h = img.height;
      if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
      else { w = Math.round(w * maxSize / h); h = maxSize; }
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function uploadProfilePicture(uid, file) {
  const dataURL = await resizeImage(file);
  await updateUser(uid, { photoURL: dataURL });
  return dataURL;
}

// ── Presence ──

async function updatePresence(uid, status) {
  const ref = doc(db, 'users', uid);
  await updateDoc(ref, {
    status,
    lastSeen: Timestamp.now(),
  });
}

function startPresenceHeartbeat(uid) {
  // Set online immediately
  updatePresence(uid, 'online');

  // Heartbeat every 60 seconds
  const interval = setInterval(() => {
    updatePresence(uid, 'online');
  }, 60000);

  // Return cleanup function
  return () => {
    clearInterval(interval);
    updatePresence(uid, 'offline');
  };
}

// ── Projects ──

async function getMyProjects(uid) {
  const q = query(
    collection(db, 'projects'),
    where('members', 'array-contains', uid)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
}

async function getAllProjects() {
  const snap = await getDocs(collection(db, 'projects'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
}

async function getProject(projectId) {
  const ref = doc(db, 'projects', projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

async function createProject({ name, description, createdBy, type }) {
  const ref = await addDoc(collection(db, 'projects'), {
    name,
    description: description || '',
    type: type || 'scrum',
    members: [createdBy],
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function createKanbanBoard(projectId) {
  const now = new Date();
  const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
  const ref = await addDoc(collection(db, 'sprints'), {
    name: 'Sprint 1',
    startDate: Timestamp.fromDate(now),
    endDate: Timestamp.fromDate(end),
    status: 'active',
    projectId,
    createdAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function updateProject(projectId, data) {
  const ref = doc(db, 'projects', projectId);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function deleteProject(projectId) {
  // Cascade-delete all sprints + items in this project
  const sprintsQ = query(
    collection(db, 'sprints'),
    where('projectId', '==', projectId)
  );
  const sprintsSnap = await getDocs(sprintsQ);

  for (const sprintDoc of sprintsSnap.docs) {
    const itemsQ = query(
      collection(db, 'sprintItems'),
      where('sprintId', '==', sprintDoc.id)
    );
    const itemsSnap = await getDocs(itemsQ);
    const batch = writeBatch(db);
    itemsSnap.docs.forEach((d) => batch.delete(d.ref));
    batch.delete(sprintDoc.ref);
    await batch.commit();
  }

  await deleteDoc(doc(db, 'projects', projectId));
}

async function addProjectMember(projectId, uid) {
  const ref = doc(db, 'projects', projectId);
  await updateDoc(ref, {
    members: arrayUnion(uid),
    updatedAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function removeProjectMember(projectId, uid) {
  const ref = doc(db, 'projects', projectId);
  await updateDoc(ref, {
    members: arrayRemove(uid),
    updatedAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function migrateToProjects() {
  // One-time migration: create "Default Project" and stamp all existing sprints
  const allUsers = await getUsers();
  const memberUids = allUsers.map((u) => u.uid);
  const createdBy = auth.currentUser?.uid || memberUids[0];

  const projectRef = await addDoc(collection(db, 'projects'), {
    name: 'Default Project',
    description: 'Auto-migrated project containing all existing sprints.',
    members: memberUids,
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Stamp all sprints with projectId
  const sprintsSnap = await getDocs(collection(db, 'sprints'));
  for (const sprintDoc of sprintsSnap.docs) {
    await updateDoc(sprintDoc.ref, { projectId: projectRef.id });
  }

  const snap = await getDoc(projectRef);
  return { id: snap.id, ...snap.data() };
}

// ── Sprints ──

async function getSprints(projectId) {
  const q = query(
    collection(db, 'sprints'),
    where('projectId', '==', projectId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
}

async function getActiveSprint(projectId) {
  const q = query(
    collection(db, 'sprints'),
    where('projectId', '==', projectId)
  );
  const snap = await getDocs(q);
  const active = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => s.status === 'active')
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
  return active.length > 0 ? active[0] : null;
}

async function createSprint({ name, startDate, endDate, projectId }) {
  const ref = await addDoc(collection(db, 'sprints'), {
    name,
    startDate: Timestamp.fromDate(startDate),
    endDate: Timestamp.fromDate(endDate),
    status: 'active',
    projectId,
    createdAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function updateSprint(id, data) {
  const ref = doc(db, 'sprints', id);
  const updates = { ...data };
  if (updates.startDate instanceof Date) {
    updates.startDate = Timestamp.fromDate(updates.startDate);
  }
  if (updates.endDate instanceof Date) {
    updates.endDate = Timestamp.fromDate(updates.endDate);
  }
  await updateDoc(ref, updates);
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function closeSprint(sprintId, options = {}) {
  const { skipItemMove = false } = options;
  // Close current sprint and record actual end date
  const sprintRef = doc(db, 'sprints', sprintId);

  // Get all items for this sprint
  const itemsQuery = query(
    collection(db, 'sprintItems'),
    where('sprintId', '==', sprintId)
  );
  const itemsSnap = await getDocs(itemsQuery);
  const allItems = itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Get current sprint to derive next sprint dates
  const sprintSnap = await getDoc(sprintRef);
  const sprintData = sprintSnap.data();
  const oldEnd = sprintData.endDate.toDate();
  const durationMs = sprintData.endDate.toDate() - sprintData.startDate.toDate();
  const newStart = oldEnd;
  const newEnd = new Date(newStart.getTime() + durationMs);

  // Parse sprint number for naming (avoid regex for DoS safety)
  const name = sprintData.name;
  let numEnd = name.length;
  while (numEnd > 0 && name[numEnd - 1] >= '0' && name[numEnd - 1] <= '9') numEnd--;
  const trailingDigits = name.slice(numEnd);
  const nextNum = trailingDigits.length > 0 ? parseInt(trailingDigits, 10) + 1 : 2;
  const namePrefix = name.slice(0, numEnd).trim() || 'Sprint';
  const nextName = `${namePrefix} ${nextNum}`;

  // Helper: get all descendants of an item
  const getDescendants = (parentId) => {
    const result = [];
    const collect = (pid) => {
      allItems.filter((i) => i.parentId === pid).forEach((child) => {
        result.push(child);
        collect(child.id);
      });
    };
    collect(parentId);
    return result;
  };

  // Build batch operations
  const batch = writeBatch(db);

  // Close sprint
  batch.update(sprintRef, {
    status: 'closed',
    closedAt: Timestamp.now(),
    closedBy: auth.currentUser?.uid || null,
  });

  // Create new sprint
  const newSprintRef = doc(collection(db, 'sprints'));
  batch.set(newSprintRef, {
    name: nextName,
    startDate: Timestamp.fromDate(newStart),
    endDate: Timestamp.fromDate(newEnd),
    status: 'active',
    projectId: sprintData.projectId || null,
    createdAt: Timestamp.now(),
  });

  if (!skipItemMove) {
    // Determine which items to move to new sprint
    const itemsToMove = new Set();
    const epicItems = allItems.filter((i) => i.type === 'epic');

    // Track items that belong to an epic (so we handle them via epic logic)
    const epicOwnedIds = new Set();
    epicItems.forEach((epic) => {
      epicOwnedIds.add(epic.id);
      getDescendants(epic.id).forEach((d) => epicOwnedIds.add(d.id));
    });

    // Process epics
    epicItems.forEach((epic) => {
      const descendants = getDescendants(epic.id);
      const allDone = descendants.length > 0 && descendants.every((d) => d.status === 'done');

      if (epic.status === 'done' && allDone) {
        // Fully completed epic: stays in closed sprint (don't move anything)
      } else {
        // Incomplete epic: move epic + uncompleted descendants
        itemsToMove.add(epic.id);
        descendants.forEach((d) => {
          if (d.status !== 'done') {
            itemsToMove.add(d.id);
          }
        });
      }
    });

    // Process non-epic-owned items (existing behavior: move if uncompleted)
    allItems.forEach((item) => {
      if (!epicOwnedIds.has(item.id) && item.status !== 'done') {
        itemsToMove.add(item.id);
      }
    });

    // Create new items and build ID mapping for parent remapping
    const oldToNewId = {};
    const movedItems = allItems.filter((i) => itemsToMove.has(i.id));

    // Sort so parents come before children for proper remapping
    const sorted = [...movedItems].sort((a, b) => {
      const aDepth = getAncestorDepth(a, allItems);
      const bDepth = getAncestorDepth(b, allItems);
      return aDepth - bDepth;
    });

    sorted.forEach((item) => {
      const newItemRef = doc(collection(db, 'sprintItems'));
      oldToNewId[item.id] = newItemRef.id;
      const newParentId = item.parentId ? (oldToNewId[item.parentId] || item.parentId) : null;
      batch.set(newItemRef, {
        ...stripId(item),
        sprintId: newSprintRef.id,
        parentId: newParentId,
        status: item.type === 'epic' ? item.status : 'not_started',
        location: 'backlog',
        updatedAt: Timestamp.now(),
      });
      // Delete old item
      batch.delete(doc(db, 'sprintItems', item.id));
    });
  }

  await batch.commit();

  const newSprintSnap = await getDoc(newSprintRef);
  return { id: newSprintSnap.id, ...newSprintSnap.data() };
}

function getAncestorDepth(item, allItems) {
  let depth = 0;
  let current = item;
  const itemMap = {};
  allItems.forEach((i) => { itemMap[i.id] = i; });
  while (current?.parentId && itemMap[current.parentId]) {
    depth++;
    current = itemMap[current.parentId];
  }
  return depth;
}

function stripId(item) {
  const { id, ...rest } = item;
  return rest;
}

// ── Sprint Items ──

async function getSprintItems(sprintId) {
  const q = query(
    collection(db, 'sprintItems'),
    where('sprintId', '==', sprintId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

const TYPE_PREFIX = { task: 'T', bug: 'B', story: 'S', epic: 'E', feature: 'F', requirement: 'R' };
const BACKLOG_ONLY_TYPES = ['epic', 'requirement'];

function generateReadableId(type) {
  const prefix = TYPE_PREFIX[type] || 'T';
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const crypto = typeof window !== 'undefined' && (window.crypto || window.msCrypto);
  const array = new Uint32Array(4);
  if (crypto && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  } else {
    for (let i = 0; i < 4; i++) array[i] = (Date.now() * (i + 1)) & 0xFFFFFFFF;
  }
  let rand = '';
  for (let i = 0; i < 4; i++) rand += chars[array[i] % chars.length];
  return `${prefix}-${rand}`;
}

async function createSprintItem(item) {
  const readableId = generateReadableId(item.type);
  const location = BACKLOG_ONLY_TYPES.includes(item.type) ? 'backlog' : (item.location || 'board');
  const ref = await addDoc(collection(db, 'sprintItems'), {
    ...item,
    readableId,
    parentId: item.parentId || null,
    assigneeId: item.assigneeId || null,
    status: item.status || 'not_started',
    location,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function updateSprintItem(id, data) {
  const ref = doc(db, 'sprintItems', id);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function deleteSprintItem(id) {
  await deleteDoc(doc(db, 'sprintItems', id));
}

async function deleteItemWithChildren(itemId, allItems) {
  const toDelete = new Set();
  const collect = (id) => {
    toDelete.add(id);
    allItems.filter((i) => i.parentId === id).forEach((child) => collect(child.id));
  };
  collect(itemId);
  const batch = writeBatch(db);
  toDelete.forEach((id) => batch.delete(doc(db, 'sprintItems', id)));
  await batch.commit();
  return [...toDelete];
}

// ── Batch Children Status ──

async function batchUpdateChildrenStatus(parentId, status, sprintId) {
  const itemsQuery = query(
    collection(db, 'sprintItems'),
    where('sprintId', '==', sprintId)
  );
  const snap = await getDocs(itemsQuery);
  const allItems = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Collect all descendants recursively
  const descendants = [];
  const collect = (pid) => {
    allItems.filter((i) => i.parentId === pid).forEach((child) => {
      descendants.push(child);
      collect(child.id);
    });
  };
  collect(parentId);

  if (descendants.length === 0) return [];

  const batch = writeBatch(db);
  descendants.forEach((item) => {
    batch.update(doc(db, 'sprintItems', item.id), {
      status,
      updatedAt: Timestamp.now(),
    });
  });
  await batch.commit();

  return descendants.map((d) => d.id);
}

// ── Templates ──

async function getTemplates() {
  const snap = await getDocs(collection(db, 'templates'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

async function getTemplate(id) {
  const ref = doc(db, 'templates', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

async function createTemplate({ name, description, items, createdBy, templateType }) {
  const ref = await addDoc(collection(db, 'templates'), {
    name,
    description: description || '',
    items: items || [],
    templateType: templateType || 'scrum',
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function updateTemplate(id, data) {
  const ref = doc(db, 'templates', id);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
}

async function deleteTemplate(id) {
  await deleteDoc(doc(db, 'templates', id));
}

async function importTemplate(templateId, sprintId, projectType) {
  const template = await getTemplate(templateId);
  if (!template || !template.items?.length) return [];

  const tType = template.templateType || 'scrum';
  if (projectType && tType !== projectType) {
    throw new Error(`This template is for ${tType === 'scrum' ? 'Scrum' : 'Kanban'} projects only`);
  }

  // Sort so parents come before children
  const sorted = [...template.items].sort((a, b) => {
    if (!a.parentTempId && b.parentTempId) return -1;
    if (a.parentTempId && !b.parentTempId) return 1;
    return (a.order || 0) - (b.order || 0);
  });

  // Get current max order
  const existingItems = await getSprintItems(sprintId);
  let maxOrder = existingItems.reduce((max, i) => Math.max(max, i.order || 0), 0);

  const tempIdToRealId = {};
  const created = [];

  for (const tItem of sorted) {
    maxOrder++;
    const parentId = tItem.parentTempId ? (tempIdToRealId[tItem.parentTempId] || null) : null;
    const item = await createSprintItem({
      type: tItem.type,
      title: tItem.title,
      description: tItem.description || '',
      storyPoints: tItem.storyPoints || null,
      parentId,
      sprintId,
      order: maxOrder,
      status: 'not_started',
      location: 'backlog',
    });
    tempIdToRealId[tItem.tempId] = item.id;
    created.push(item);
  }

  return created;
}

// ── Sprint History ──

async function getClosedSprints(projectId) {
  const q = query(
    collection(db, 'sprints'),
    where('projectId', '==', projectId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => s.status === 'closed')
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
}

async function deleteClosedSprint(sprintId) {
  const batch = writeBatch(db);
  batch.delete(doc(db, 'sprints', sprintId));
  const itemsQuery = query(
    collection(db, 'sprintItems'),
    where('sprintId', '==', sprintId)
  );
  const itemsSnap = await getDocs(itemsQuery);
  itemsSnap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

async function deleteAllClosedSprints(projectId) {
  const closed = await getClosedSprints(projectId);
  for (const sprint of closed) {
    await deleteClosedSprint(sprint.id);
  }
}

const api = {
  signIn,
  signOut: signOutUser,
  onAuthStateChanged,
  getUserProfile,
  getUsers,
  createUser,
  createUserProfile,
  updateUser,
  deleteUser,
  uploadProfilePicture,
  updatePresence,
  startPresenceHeartbeat,
  // Projects
  getMyProjects,
  getAllProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  createKanbanBoard,
  addProjectMember,
  removeProjectMember,
  migrateToProjects,
  // Sprints
  getSprints,
  getActiveSprint,
  createSprint,
  updateSprint,
  closeSprint,
  getSprintItems,
  createSprintItem,
  updateSprintItem,
  deleteSprintItem,
  deleteItemWithChildren,
  batchUpdateChildrenStatus,
  // Templates
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  importTemplate,
  getClosedSprints,
  deleteClosedSprint,
  deleteAllClosedSprints,
};

export default api;
